import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout.jsx';
import { api, uploadUrl } from '../../api/client.js';
import { Swal2 } from '../../lib/swal.js';
import { MORPH_BTN, MORPH_BTN_SM } from '../../lib/ui.js';

const empty = { nama: '', deskripsi: '', benefit: '', target_audience: '', durasi_proses: '', icon: 'pets', sort_order: 0, is_active: true };
const ICONS = ['menu_book', 'auto_awesome', 'emoji_events', 'mic', 'pets', 'school', 'diversity_3', 'celebration'];

const DESKRIPSI_LIMIT = 120;
const SUB_DESKRIPSI_LIMIT = 110;

function CharCounter({ text, limit }) {
  const len = (text || '').length;
  const over = len > limit;
  return (
    <span className={`text-[11px] ${over ? 'text-amber-500 font-semibold' : 'text-neutral-400'}`}>
      {len} karakter{over ? ` (disarankan maks ${limit})` : ''}
    </span>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors duration-300 ease-out hover:opacity-90 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900 focus-visible:ring-forest-500/50 ${checked ? '' : 'bg-neutral-300 dark:bg-neutral-700'}`}
      style={{ transition: 'background-color 300ms, transform 150ms var(--ease-spring)', background: checked ? 'var(--accent, #4A7A3A)' : undefined }}
    >
      <span
        className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-md"
        style={{ transform: checked ? 'translateX(1.25rem)' : 'translateX(0)', transition: 'transform 300ms var(--ease-spring)' }}
      />
    </button>
  );
}

export default function LayananForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [subEnabled, setSubEnabled] = useState(false);
  const [subLayanan, setSubLayanan] = useState([]);

  useEffect(() => {
    if (isEdit) {
      api.get(`/admin/layanan/${id}`).then(({ data }) => {
        setForm({ ...data.item, is_active: !!data.item.is_active, benefit: (data.item.benefit || []).join('\n') });
        setPreview(data.item.image ? uploadUrl(data.item.image) : '');
        const subs = data.item.sub_layanan || [];
        if (subs.length > 0) {
          setSubEnabled(true);
          setSubLayanan(subs.map((s) => ({ nama: s.nama, deskripsi: s.deskripsi || '' })));
        }
      });
    }
  }, [id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onFile = (e) => {
    const f = e.target.files[0];
    setFile(f);
    if (f) setPreview(URL.createObjectURL(f));
  };

  const toggleSub = (val) => {
    setSubEnabled(val);
    if (val && subLayanan.length === 0) {
      setSubLayanan([{ nama: '', deskripsi: '' }]);
    }
  };
  const addSubRow = () => setSubLayanan((rows) => [...rows, { nama: '', deskripsi: '' }]);
  const removeSubRow = (idx) => setSubLayanan((rows) => rows.filter((_, i) => i !== idx));
  const updateSubRow = (idx, field, value) =>
    setSubLayanan((rows) => rows.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (k !== 'image' && k !== 'id' && k !== 'sub_layanan') fd.append(k, v); });
      if (file) fd.append('image', file);
      const subToSend = subEnabled ? subLayanan.filter((s) => s.nama.trim()) : [];
      fd.append('sub_layanan', JSON.stringify(subToSend));

      if (isEdit) await api.put(`/admin/layanan/${id}`, fd);
      else await api.post('/admin/layanan', fd);
      Swal2.toast('success', isEdit ? 'Berhasil diperbarui.' : 'Berhasil ditambahkan.');
      navigate('/admin/layanan');
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menyimpan.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 rounded-xl border text-sm outline-none bg-neutral-50 dark:bg-neutral-800 dark:text-white border-neutral-200 dark:border-neutral-700 focus:border-forest-500 focus:ring-2 focus:ring-forest-500/20';
  const labelCls = 'block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-1.5';

  return (
    <AdminLayout title={isEdit ? 'Edit Layanan' : 'Tambah Layanan'}>
      <form onSubmit={submit} className="max-w-2xl bg-white dark:bg-neutral-900 rounded-2xl border border-black/5 dark:border-white/5 p-6 space-y-4">
        {error && <div className="p-3 rounded-xl text-sm font-semibold bg-red-50 text-red-600 dark:bg-red-950/30">{error}</div>}

        <div>
          <label className={labelCls}>Nama Layanan</label>
          <input required className={inputCls} value={form.nama} onChange={set('nama')} />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className={`${labelCls} mb-0`}>Deskripsi Singkat</label>
            <CharCounter text={form.deskripsi} limit={DESKRIPSI_LIMIT} />
          </div>
          <textarea rows={2} className={`${inputCls} resize-none`} value={form.deskripsi || ''} onChange={set('deskripsi')} />
        </div>
        <div>
          <label className={labelCls}>Benefit (satu poin per baris)</label>
          <textarea rows={4} className={`${inputCls} resize-none`} value={form.benefit || ''} onChange={set('benefit')} placeholder={'Edukasi langsung berinteraksi dengan satwa\nMateri sesuai kurikulum sekolah'} />
        </div>
        <div>
          <label className={labelCls}>Target Audiens</label>
          <input className={inputCls} value={form.target_audience || ''} onChange={set('target_audience')} />
        </div>
        <div>
          <label className={labelCls}>Alur Proses (pisahkan dengan →)</label>
          <input className={inputCls} value={form.durasi_proses || ''} onChange={set('durasi_proses')} placeholder="Konsultasi → Survei Lokasi → Hari H → Laporan" />
        </div>
        <div>
          <label className={labelCls}>Ikon</label>
          <div className="flex flex-wrap gap-2">
            {ICONS.map((ic) => (
              <button type="button" key={ic} onClick={() => setForm((f) => ({ ...f, icon: ic }))}
                className={`w-11 h-11 rounded-xl flex items-center justify-center border transition-all duration-300 hover:rounded-2xl active:rounded-lg active:scale-90 ${form.icon === ic ? 'border-forest-500 bg-forest-500/10 text-forest-500' : 'border-neutral-200 dark:border-neutral-700 text-neutral-400 hover:border-forest-500/50 hover:text-forest-500 hover:bg-forest-500/5'}`}>
                <span className="material-symbols-outlined">{ic}</span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className={labelCls}>Gambar (opsional)</label>
          {preview && <img src={preview} className="w-full h-40 object-cover rounded-xl mb-2" />}
          <input type="file" accept="image/*" onChange={onFile} className="text-sm text-neutral-500" />
        </div>
        <div className="grid grid-cols-2 gap-4 items-center">
          <div>
            <label className={labelCls}>Urutan</label>
            <input type="number" className={inputCls} value={form.sort_order} onChange={set('sort_order')} />
          </div>
          <label className="flex items-center gap-2 mt-6 text-sm font-semibold text-neutral-700 dark:text-white">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} className="w-4 h-4 accent-forest-500" />
            Tampilkan layanan ini
          </label>
        </div>

        {/* ══════ Sub Layanan ══════ */}
        <div className="pt-2 border-t border-neutral-100 dark:border-neutral-800">
          <div className="flex items-center justify-between mt-4 mb-3">
            <div>
              <label className={`${labelCls} mb-0.5`}>Sub Layanan</label>
              <p className="text-[11px] text-neutral-400">Submenu di dalam layanan ini, mis. "Waterboom", "Waterschool".</p>
            </div>
            <Toggle checked={subEnabled} onChange={toggleSub} />
          </div>

          {subEnabled && (
            <div className="space-y-3">
              {subLayanan.map((row, idx) => {
                const descLen = (row.deskripsi || '').length;
                const over = descLen > SUB_DESKRIPSI_LIMIT;
                return (
                  <div key={idx} className="rounded-xl border border-neutral-200 dark:border-neutral-700 p-3 space-y-2 bg-neutral-50/50 dark:bg-neutral-800/40">
                    <div className="flex items-center gap-2">
                      <input
                        placeholder="Nama sub layanan"
                        className={`${inputCls} flex-1`}
                        value={row.nama}
                        onChange={(e) => updateSubRow(idx, 'nama', e.target.value)}
                      />
                      <button type="button" onClick={() => removeSubRow(idx)}
                        className={`w-9 h-9 flex-shrink-0 rounded-lg flex items-center justify-center bg-red-50 text-red-500 dark:bg-red-950/30 ${MORPH_BTN_SM}`}>
                        <span className="material-symbols-outlined text-base">delete</span>
                      </button>
                    </div>
                    <div>
                      <textarea
                        rows={2}
                        placeholder="Deskripsi singkat sub layanan ini"
                        className={`${inputCls} resize-y`}
                        style={{ minHeight: '4.5rem' }}
                        value={row.deskripsi}
                        onChange={(e) => updateSubRow(idx, 'deskripsi', e.target.value)}
                      />
                      <div className="flex justify-end mt-1">
                        <span className={`text-[11px] ${over ? 'text-amber-500 font-semibold' : 'text-neutral-400'}`}>
                          {descLen} karakter{over ? ` (disarankan maks ${SUB_DESKRIPSI_LIMIT})` : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}

              <button type="button" onClick={addSubRow}
                className={`w-full py-2.5 rounded-xl text-sm font-semibold border border-dashed border-neutral-300 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:border-forest-500 hover:text-forest-500 flex items-center justify-center gap-1.5 ${MORPH_BTN_SM}`}>
                <span className="material-symbols-outlined text-base">add</span> Tambah Sub Layanan
              </button>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <button disabled={saving} type="submit" className={`px-6 py-3 rounded-xl font-bold text-white text-sm ${MORPH_BTN}`} style={{ background: 'var(--accent)' }}>{saving ? 'Menyimpan...' : 'Simpan'}</button>
          <button type="button" onClick={() => navigate('/admin/layanan')} className={`px-6 py-3 rounded-xl font-bold text-sm bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-white ${MORPH_BTN}`}>Batal</button>
        </div>
      </form>
    </AdminLayout>
  );
}
